import { useState } from 'react'
import CustomBottomSheet from 'components/bottomSheet'
import BankAccountCard from 'components/bankAccountCard'
import WithDrawWalletModal from 'components/modals/withDrawWallets'
import DotsWhiteLoading from 'components/loadings/dotsWhiteLoading'
import useGetCards from 'pages/loans/withDraw/hooks/useGetCards'
import { toman } from 'utilities/helper/format'
import { SettlementPlan } from '../settlementPlan'
import { SettleMode } from '../hooks/useSettleAndExit'

function Line({
    label,
    value,
    tone,
}: {
    label: string
    value: string
    tone?: 'strong'
}) {
    return (
        <div className=" flex items-start justify-between gap-3 border-b border-divider py-2 last:border-b-0">
            <span className=" shrink-0 text-[11px] text-text-secondary">
                {label}
            </span>
            <span
                className={` text-left ${
                    tone === 'strong'
                        ? ' text-sm font-bold text-heading'
                        : ' text-[11px] font-bold text-text-body'
                }`}
            >
                {value}
            </span>
        </div>
    )
}

/**
 * The last screen of «قبول» when the fund still owes the leaving member money.
 *
 * Two ways out and they are genuinely different, so they are two buttons
 * rather than a button and a toggle — the same shape as the اعطای وام wizard,
 * for the same reason:
 *
 *  - **پرداخت و تسویه** moves the money from «موجودی نزد همیان» to the
 *    member's account, then closes the membership.
 *  - **فقط ثبت شود** records a payout the manager makes themselves. The books
 *    end up identical; only the transfer is not ours to make.
 *
 * The destination account is on the face of the sheet rather than inside the
 * picker because it serves both buttons: Hamyan reads it for the first, and
 * the manager reads the digits off it for the second.
 */
export default function SettleSheet({
    isOpen,
    onDismiss,
    request,
    plan,
    isSettling,
    onSettle,
}: {
    isOpen: boolean
    onDismiss: () => void
    request?: IAdminSettlementResult
    plan: Extract<SettlementPlan, { kind: 'payout' }>
    isSettling: boolean
    onSettle: (mode: SettleMode, bankAccount?: number) => void
}) {
    const [isPickingCard, setIsPickingCard] = useState(false)
    const [, setSelectedBankNumber] = useState<number>(0)

    const member = request?.requesting_user.member
    const memberName = member ? `${member.first_name} ${member.last_name}` : ''
    const {
        data,
        isLoading,
        activeAccounts,
        otherAccounts,
        handleSelectCard,
        reallySelectedCard,
    } = useGetCards(
        request?.requesting_user.id,
        member?.id,
        setSelectedBankNumber,
        // the account the member named on their own exit request; anything
        // else would be the app overruling them
        request?.selected_card_number?.id
    )

    const unpaidInstallments = request?.requesting_user.debit_amount ?? 0
    const canPay = plan.canPayFromHamyan && !!reallySelectedCard

    return (
        <>
            <CustomBottomSheet isOpen={isOpen} onDismiss={onDismiss}>
                <div className=" max-h-[75vh] overflow-y-auto pb-2">
                    <p className=" text-center text-sm font-bold text-heading">
                        تسویه و خروج {memberName}
                    </p>

                    <div className=" mt-3 rounded-2xl border border-stroke bg-white p-4">
                        <Line
                            label="مبلغ بازگشتی به عضو"
                            value={toman(plan.amount)}
                            tone="strong"
                        />
                        <Line
                            label="سرمایهٔ عضو در صندوق"
                            value={toman(request?.requesting_user.capital ?? 0)}
                        />
                        {unpaidInstallments > 0 && (
                            <Line
                                label="اقساط پرداخت‌نشده"
                                value={toman(unpaidInstallments)}
                            />
                        )}
                    </div>

                    {unpaidInstallments > 0 && (
                        <p className=" mt-2 rounded-lg bg-warning-soft px-2.5 py-2 text-[11px] leading-5 text-warning-on">
                            اقساط پرداخت‌نشدهٔ این عضو هنگام تسویه از همین مبلغ
                            کسر و تسویه می‌شود.
                        </p>
                    )}

                    <div className=" mt-3 rounded-2xl border border-stroke bg-white p-4">
                        <p className=" mb-2 text-xs font-bold text-brand-deep">
                            حساب مقصد
                        </p>
                        {reallySelectedCard ? (
                            <BankAccountCard
                                account={reallySelectedCard}
                                belongsToBorrower={
                                    member?.id
                                        ? reallySelectedCard.member ===
                                          member.id
                                        : undefined
                                }
                                borrowerName={memberName}
                                onChange={() => setIsPickingCard(true)}
                                source="exit_request_settle"
                            />
                        ) : (
                            <button
                                type="button"
                                onClick={() => setIsPickingCard(true)}
                                className=" w-full rounded-xl bg-canvas px-3 py-2.5 text-right text-xs text-text-body"
                            >
                                {isLoading
                                    ? 'در حال خواندن حساب‌ها…'
                                    : 'انتخاب شماره حساب'}
                            </button>
                        )}
                        {!isLoading && !reallySelectedCard && (
                            <p className=" mt-2 rounded-lg bg-warning-soft px-2.5 py-2 text-[11px] leading-5 text-warning-on">
                                {memberName} هنوز شماره حسابی ثبت نکرده. یا از
                                ایشان بخواهید شماره حسابش را ثبت کند، یا پول را
                                خودتان بدهید و «فقط ثبت شود» را بزنید.
                            </p>
                        )}
                    </div>

                    {plan.blockedReason && (
                        <p className=" mt-2 rounded-lg bg-warning-soft px-2.5 py-2 text-[11px] leading-5 text-warning-on">
                            {plan.blockedReason}
                        </p>
                    )}

                    <div className=" mt-4 space-y-2">
                        {plan.canPayFromHamyan && (
                            <button
                                type="button"
                                disabled={!canPay || isSettling}
                                onClick={() =>
                                    onSettle('pay', reallySelectedCard?.id)
                                }
                                className=" w-full rounded-full bg-green-1 py-3 text-[15px] font-bold text-white transition-transform active:scale-[0.99] disabled:opacity-50 motion-reduce:transition-none"
                            >
                                {isSettling ? (
                                    <DotsWhiteLoading />
                                ) : (
                                    'پرداخت و تسویه'
                                )}
                            </button>
                        )}
                        <button
                            type="button"
                            disabled={isSettling}
                            onClick={() => onSettle('record')}
                            className=" w-full rounded-full border border-brand bg-white py-2.5 text-[13px] font-bold text-brand-deep disabled:opacity-50"
                        >
                            فقط ثبت شود (پول را خودم می‌دهم)
                        </button>
                        <button
                            type="button"
                            disabled={isSettling}
                            onClick={onDismiss}
                            className=" w-full py-2 text-[13px] text-text-secondary disabled:opacity-50"
                        >
                            بعداً
                        </button>
                    </div>
                </div>
            </CustomBottomSheet>

            <WithDrawWalletModal
                show={isPickingCard}
                onHide={() => setIsPickingCard(false)}
                // optional the whole way down: a member with no registered
                // account comes back as an empty `receivers`
                data={data?.receivers?.[0]?.bankaccounts}
                activeAccounts={activeAccounts}
                otherAccounts={otherAccounts}
                selectedUser={memberName}
                setSelectedBankNumber={setSelectedBankNumber}
                handleSelectCard={handleSelectCard}
            />
        </>
    )
}
